// Per-event and per-market CASH / GAME controls (SM-446).
//
// The rule this component exists to make visible:
//
//     effective = event.<mode>_enabled AND market.<mode>_enabled
//                 (and, for GAME, the F5 visibility flag)
//
// Three separate facts, and the panel renders them as three. Collapsing
// them into one toggle is the failure mode: an operator who turns GAME on
// for a market and sees nothing happen has to be told WHICH of the three
// is still saying no, otherwise the next thing they do is turn it off and
// on again.
//
// What disabling a mode does — and does not do — matters just as much.
// Phase 04 settled it (decision-log Q3): a disable is a STOP-NEW gate.
// The channel row, the book, resting orders and every position survive
// it; order-service refuses new placements on a disabled channel, and
// nothing is cancelled. So this panel warns about what is still live
// rather than offering a cancel — a cancel-all loop invented in the
// frontend is exactly what the phase forbids, and it would destroy value
// the backend deliberately preserved (G1).

import { useQuery } from "@tanstack/react-query";

import { Badge } from "@/components/ui/Badge";
import { TechHint } from "@/components/ui/TechHint";
import { cn } from "@/lib/cn";
import { fetchMarketChannels, keys } from "@/lib/queries";
import type { TradingChannelItem } from "@/lib/types";

export type TradingMode = "CASH" | "GAME";

export const TRADING_MODES: readonly TradingMode[] = ["CASH", "GAME"] as const;

export const MODE_LABEL: Record<TradingMode, string> = {
  CASH: "Cash",
  GAME: "Game",
};

/**
 * A market or event flag as it should be READ.
 *
 * Absent means the row predates SM-425, and the backfill default is CASH
 * on / GAME off — the same thing every existing market got. Defaulting
 * GAME to `true` on a missing field would silently open a book.
 */
export function modeFlag(
  row: { cash_enabled?: boolean; game_enabled?: boolean } | undefined,
  mode: TradingMode,
): boolean {
  if (!row) return mode === "CASH";
  const v = mode === "CASH" ? row.cash_enabled : row.game_enabled;
  return v ?? mode === "CASH";
}

/** event AND market — the availability rule, minus the F5 flag the server folds in. */
export function localEffective(
  event: { cash_enabled?: boolean; game_enabled?: boolean } | undefined,
  market: { cash_enabled?: boolean; game_enabled?: boolean } | undefined,
  mode: TradingMode,
): boolean {
  return modeFlag(event, mode) && modeFlag(market, mode);
}

function StateDot({ on, className }: { on: boolean; className?: string }) {
  return (
    <span
      aria-hidden
      className={cn("inline-block h-1.5 w-1.5 shrink-0 rounded-full", on ? "bg-yes" : "bg-t-4", className)}
    />
  );
}

/** One fact, one row: what it is, whether it is on, where it is set. */
function FactRow({ label, on, note, onWord, offWord }: {
  label: string;
  on: boolean;
  note: string;
  onWord: string;
  offWord: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <StateDot on={on} className="mt-1.5" />
      <div className="min-w-0">
        <p className="text-2xs font-semibold uppercase tracking-wide text-t-3">{label}</p>
        <p className={cn("text-xs font-semibold", on ? "text-yes" : "text-t-3")}>
          {on ? onWord : offWord}
        </p>
        <p className="mt-0.5 text-2xs leading-relaxed text-t-3">{note}</p>
      </div>
    </div>
  );
}

export interface TradingModePanelProps {
  /** Logical market id. Also the CASH channel id, by construction. */
  marketID: string;
  market: { cash_enabled?: boolean; game_enabled?: boolean } | undefined;
  event: { cash_enabled?: boolean; game_enabled?: boolean } | undefined;
  /** Pending market-level edits, so the panel previews what Save will do. */
  draft?: Partial<Record<"cash_enabled" | "game_enabled", boolean>>;
  onChange?: (mode: TradingMode, next: boolean) => void;
  disabled?: boolean;
  /**
   * Open orders on this market, already fetched by the caller. Used only
   * to warn before a disable — this panel never cancels anything.
   */
  openOrders?: { mode?: string }[];
}

export function TradingModePanel({
  marketID,
  market,
  event,
  draft,
  onChange,
  disabled,
  openOrders,
}: TradingModePanelProps) {
  const channels = useQuery({
    queryKey: keys.marketChannels(marketID),
    queryFn: () => fetchMarketChannels(marketID),
    enabled: !!marketID,
  });

  const merged = { ...market, ...draft };

  return (
    <section className="space-y-4" aria-label="Trading modes">
      <div>
        <h4 className="text-2xs font-semibold uppercase tracking-wide text-t-3">Trading modes</h4>
        <p className="mt-1 max-w-[75ch] text-2xs leading-relaxed text-t-3">
          Every market is one shared market — content, comments, outcomes and
          resolution never split. Only the order book splits: each enabled mode gets
          its own trading channel, its own orders and its own positions, and the
          two can never match against each other.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {TRADING_MODES.map((mode) => (
          <ModeCard
            key={mode}
            mode={mode}
            marketID={marketID}
            eventOn={modeFlag(event, mode)}
            marketOn={modeFlag(merged, mode)}
            savedMarketOn={modeFlag(market, mode)}
            channel={(channels.data ?? []).find((c) => c.trading_mode === mode)}
            channelsLoaded={channels.isSuccess}
            onChange={onChange ? (next) => onChange(mode, next) : undefined}
            disabled={disabled}
            openOrders={openOrders}
          />
        ))}
      </div>
    </section>
  );
}

function ModeCard({
  mode,
  marketID,
  eventOn,
  marketOn,
  savedMarketOn,
  channel,
  channelsLoaded,
  onChange,
  disabled,
  openOrders,
}: {
  mode: TradingMode;
  marketID: string;
  eventOn: boolean;
  marketOn: boolean;
  savedMarketOn: boolean;
  channel: TradingChannelItem | undefined;
  channelsLoaded: boolean;
  onChange?: (next: boolean) => void;
  disabled?: boolean;
  openOrders?: { mode?: string }[];
}) {
  const local = eventOn && marketOn;
  // A market's CASH channel id IS its market id (the legacy identity);
  // a GAME channel only exists once GAME has been enabled at least once.
  const channelID = channel?.channel_id ?? (mode === "CASH" ? marketID : undefined);
  // An order row with no mode predates phase 04, and those are CASH (G3).
  const openHere = (openOrders ?? []).filter((o) => (o.mode || "CASH") === mode).length;
  const willDisable = savedMarketOn && !marketOn;

  return (
    <div className="rounded-md border border-line-1 bg-bg-2/40 p-4" aria-label={`${mode} trading mode`}>
      <header className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h5 className="text-xs font-semibold text-t-1">{MODE_LABEL[mode]}</h5>
          <Badge tone={local ? "success" : "muted"}>{local ? "Available" : "Unavailable"}</Badge>
        </div>
        <label className="flex items-center gap-2 text-2xs text-t-2">
          <input
            type="checkbox"
            checked={marketOn}
            disabled={disabled || !onChange}
            onChange={(e) => onChange?.(e.target.checked)}
            aria-label={`${MODE_LABEL[mode]} enabled for this market`}
          />
          Market
        </label>
      </header>

      <div className="space-y-3">
        <FactRow
          label="Event setting"
          on={eventOn}
          onWord="Enabled"
          offWord="Disabled"
          note="Set on the parent event. Disabling it there takes the mode away from every market under it."
        />
        <FactRow
          label="Market setting"
          on={marketOn}
          onWord="Enabled"
          offWord="Disabled"
          note="This market's own switch — the toggle above."
        />
        <FactRow
          label="Effective availability"
          on={local}
          onWord="Both settings agree"
          offWord={eventOn ? "Blocked by the market setting" : "Blocked by the event setting"}
          note={
            mode === "GAME"
              ? "Both settings must be on, and the game.visibility flag (F5) must be on too — the server folds that in."
              : "Both settings must be on."
          }
        />
      </div>

      <dl className="mt-3 space-y-1 border-t border-line-1 pt-3 text-2xs">
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-t-4">Trading channel</dt>
          <dd className="min-w-0 truncate text-t-2">
            {channelID ? (
              <TechHint tech={channelID}>{shorten(channelID)}</TechHint>
            ) : channelsLoaded ? (
              "Not created yet"
            ) : (
              "…"
            )}
          </dd>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <dt className="text-t-4">Channel state</dt>
          <dd className="text-t-2">{channelState(channel, channelsLoaded, mode)}</dd>
        </div>
      </dl>

      {willDisable && (
        <p className="mt-3 rounded-md border border-warn/40 bg-warn/5 px-2.5 py-2 text-2xs leading-relaxed text-warn">
          {disableWarning(mode, openHere)}
        </p>
      )}
    </div>
  );
}

/**
 * What the server says about this channel right now — the effective
 * verdict, not this panel's arithmetic.
 *
 * A GAME row that does not exist yet is not "off": it has never been
 * created, and saying so tells the operator that enabling will mint one.
 */
function channelState(
  channel: TradingChannelItem | undefined,
  loaded: boolean,
  mode: TradingMode,
): string {
  if (!loaded) return "…";
  if (!channel) {
    return mode === "GAME"
      ? "No channel yet — enabling GAME creates one"
      : "Not reported";
  }
  if (!channel.enabled) return "Stored switch off";
  if (!channel.effective_enabled) {
    return mode === "GAME" ? "Enabled, but not effective (event flag or F5)" : "Enabled, but not effective";
  }
  return channel.effective_published ? "Live — book and streams public" : "Effective, not published";
}

/**
 * The disable warning.
 *
 * Deliberately not "this will cancel N orders", because it will not.
 * Phase 04 made a disable a stop-new gate: resting orders keep resting,
 * their owners can still cancel them, fills still settle, and positions
 * still redeem. Telling an operator otherwise would make them expect a
 * cleanup that never happens.
 */
function disableWarning(mode: TradingMode, openHere: number): string {
  // Two whole sentences rather than a shared prefix plus a suffix: a
  // half-sentence is not a translatable unit, and the i18n extractor
  // would file each fragment as its own phrase.
  if (openHere > 0) {
    return `Disabling ${MODE_LABEL[mode]} stops new orders on this channel. It does not cancel anything: resting orders keep resting and stay cancellable by their owners, fills still settle, and positions still redeem. There are currently ${openHere} open ${MODE_LABEL[mode]} orders on this market.`;
  }
  return `Disabling ${MODE_LABEL[mode]} stops new orders on this channel. It does not cancel anything: resting orders keep resting and stay cancellable by their owners, fills still settle, and positions still redeem. No open orders are listed on this market right now.`;
}

function shorten(id: string): string {
  return id.length > 14 ? `${id.slice(0, 6)}…${id.slice(-4)}` : id;
}

/**
 * A one-line "which books does this have" chip pair, for list rows.
 *
 * Shows the row's OWN flags, not the effective verdict — a list cannot
 * afford one channels request per row, and claiming an effective answer
 * it did not read would be worse than showing the setting it did. The
 * label says "set", and the detail panel is where the effective verdict
 * lives.
 *
 * A row with the default shape (CASH on, GAME off) renders CASH only:
 * every pre-epic market looks like that, and a "GAME: off" chip on every
 * row would be noise on thousands of rows to say nothing.
 */
export function ModeChips({
  row,
  className,
}: {
  row: { cash_enabled?: boolean; game_enabled?: boolean } | undefined;
  className?: string;
}) {
  const on = TRADING_MODES.filter((m) => modeFlag(row, m));
  if (on.length === 1 && on[0] === "CASH") {
    return (
      <span className={cn("shrink-0 uppercase text-t-4", className)} title="Cash only (the default)">
        cash
      </span>
    );
  }
  return (
    <span className={cn("shrink-0 uppercase text-t-4", className)}>
      {on.length === 0 ? "no modes set" : on.map((m) => m.toLowerCase()).join(" + ")}
    </span>
  );
}
